import { lotsServices, type ApiStockLot } from "../../../../../api/lots";
import type { ActOfCheckingAvailabilityContent } from "../../../../../api/actOfCheckingAvailability";
import { storePlacesServices } from "../../../../../api/storePlaces";

/** Результат поиска строки АПН по скану партии. */
export type StockPartialInventoryLotScanResult =
    | { type: "notFound" }
    | { type: "contentNotFound"; lot: ApiStockLot; hasWarehouseRemainders: boolean }
    | { type: "ambiguous"; lot: ApiStockLot; contents: ActOfCheckingAvailabilityContent[] }
    | { type: "single"; content: ActOfCheckingAvailabilityContent };

/** Параметры разрешения скана партии на карточке АПН. */
export interface ResolveStockPartialInventoryLotScanParams {
    /** Отсканированный номер партии (`SoNum`). */
    barcode: string;
    /** Строки состава текущего АПН. */
    contents: ActOfCheckingAvailabilityContent[];
    /** Код склада АПН — для проверки остатков партии на складе документа. */
    divisionCode: number;
}

/**
 * Ищет партию по скану через `stock/lots.loadByFilter` (`SoNum`) и сопоставляет
 * со строками состава текущего АПН по `ParentLotCode`. \
 * Если строки нет — проверяет положительные остатки партии на складе АПН
 * (`storePlaces.loadRemaindersByFilter` + `DivisionCode` + `LotCodes` + `InContextOfLot`).
 * @param params параметры скана
 */
export async function resolveStockPartialInventoryLotScan(
    params: ResolveStockPartialInventoryLotScanParams,
): Promise<StockPartialInventoryLotScanResult> {
    const { barcode, contents, divisionCode } = params;
    const lots = uniqueLots(await lotsServices.load({ flt: { SoNum: barcode } }));

    if (lots.length === 0) {
        return { type: "notFound" };
    }

    const matchedContents = lots.flatMap((lot) =>
        contents.filter((content) => content.ParentLotCode === lot.LotCode).map((content) => ({ lot, content })),
    );

    if (matchedContents.length === 0) {
        const lot = lots[0];
        const hasWarehouseRemainders = await hasPositiveRemaindersOnDivision(lot.LotCode, divisionCode);

        return { type: "contentNotFound", lot, hasWarehouseRemainders };
    }

    if (matchedContents.length > 1) {
        return {
            type: "ambiguous",
            lot: matchedContents[0].lot,
            contents: matchedContents.map((item) => item.content),
        };
    }

    return { type: "single", content: matchedContents[0].content };
}

async function hasPositiveRemaindersOnDivision(lotCode: number, divisionCode: number): Promise<boolean> {
    if (!isValidCode(lotCode) || !isValidCode(divisionCode)) {
        return false;
    }

    const remainders = await storePlacesServices.remainders.load({
        filter: {
            LotCodes: [lotCode],
            DivisionCode: divisionCode,
            InContextOfLot: true,
            OnlyPositiveRemainders: true,
        },
    });

    return remainders.some((item) => isValidCode(item.LotCode) && item.Qty > 0);
}

function uniqueLots(lots: ApiStockLot[]): ApiStockLot[] {
    const result: ApiStockLot[] = [];
    const seen = new Set<number>();

    lots.forEach((lot) => {
        if (!seen.has(lot.LotCode)) {
            seen.add(lot.LotCode);
            result.push(lot);
        }
    });

    return result;
}

function isValidCode(code: number): boolean {
    return code !== 0;
}
